import clsx from 'clsx'
import { useRef, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { dateToYYYYMMDD } from '@/src/general/primitives/dates/dayjs'
import { mapPropsVariants } from '@/src/general/utils/mapPropsVariants'
import { colors, type TMainColor } from '@/src/styles/colors'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { Button } from '../../button/default'
import { inputVariants, type TInputNativeSlots, type TInputVariantProps } from '../variants/inputVariants'
import type { TDateTimePickerProps } from './DateTimePicker.props'

const nativeDatePickerInputClassName = 'native-date-picker-input'

/**
 * Hide the native icon for the date picker on Firefox
 */
const getHideNativeIconStyle = (color: TMainColor = 'default') => `
  .${nativeDatePickerInputClassName} {
    color: ${colors[color]['text-contrast-default'].DEFAULT};
  }

  .${nativeDatePickerInputClassName}::-webkit-calendar-picker-indicator {
    position: absolute;
    margin: 0;
    padding: 0;
    visibility: hidden;
    opacity: 0;
  }

  /**
   * Firefox specific style to hide the native icon
   * @see https://bugzilla.mozilla.org/show_bug.cgi?id=1817839
   */
  @-moz-document url-prefix() {
    .${nativeDatePickerInputClassName} {
      -webkit-text-fill-color: ${colors[color]['text-contrast-default'].DEFAULT};
      color: transparent;
    }
  }
`

export const DateTimePicker = (
  originalProps: TDateTimePickerProps & {
    classNames?: SlotsToClasses<TInputNativeSlots>
  } & TInputVariantProps,
): ReactNode => {
  const [props, variantProps] = mapPropsVariants(originalProps, inputVariants.variantKeys)

  const { testID, value, onChange, ariaLabel, className, classNames, min, max, mode = 'date' } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const { locale } = useIntl()

  const slots = inputVariants({ ...variantProps })

  /**
   * Use default color when the value is defined,
   * otherwise use the color from the variant props to reproduce the input placeholder color
   */
  const forceTextColor = value ? 'default' : variantProps.color

  return (
    <div
      data-testid={testID ?? TEST_ID.ELEMENT.DATETIMEPICKER}
      className={slots.inputWrapper({
        class: clsx('w-full', className, classNames?.inputWrapper),
      })}
    >
      <style>{getHideNativeIconStyle(forceTextColor)}</style>
      <input
        ref={inputRef}
        data-testid={`${testID ?? TEST_ID.ELEMENT.DATETIMEPICKER}-input`}
        aria-label={ariaLabel}
        className={slots.input({
          class: clsx(nativeDatePickerInputClassName, classNames?.input),
        })}
        id="dateTimePicker"
        name="dateTimePicker"
        type={mode}
        value={value ? dateToYYYYMMDD(value) : ''}
        lang={locale}
        autoComplete="on"
        onChange={(e) => {
          if (e.target.value === '') {
            onChange(undefined)
          } else return onChange(new Date(e.target.value))
        }}
        min={min && dateToYYYYMMDD(min)}
        max={max && dateToYYYYMMDD(max)}
      />
      <Button
        isIconOnly
        startIcon={ICON.CALENDAR}
        colors="default"
        variant="light"
        contrast="low"
        size="sm"
        className={clsx('-mr-3', slots.icon({ class: classNames?.icon }))}
        onPress={() => {
          if (inputRef?.current && 'showPicker' in inputRef.current) {
            inputRef.current.showPicker()
          }
        }}
      />
    </div>
  )
}
